import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { PACK_HANDOFF_GRACE_MS, PACK_HANDOFF_PARAM, packHandoffPending } from './pack-handoff';

/**
 * Local busy state for the window between accepting a pack and the announcement turn becoming visible
 * on the conversation (see `lib/pack-handoff`).
 *
 * Armed off the marker the accept landing's CTA puts on the URL — the only evidence the chat has that
 * this arrival came from an accept rather than from a user opening an old thread. It is consumed once:
 * the param is stripped immediately, so a reload or a shared URL spins nothing, and the flag then lives
 * in state until it expires. A real turn taking over (`remoteTurnActive`, a streamed row) outranks it
 * at the render site, so nothing has to clear it.
 */
export function usePackHandoff(): boolean {
  const router = useRouter();
  const [armedAt, setArmedAt] = useState<number | null>(null);
  const consumed = useRef(false);

  useEffect(() => {
    if (!router.isReady || consumed.current) return;
    if (router.query[PACK_HANDOFF_PARAM] !== '1') return;
    consumed.current = true;
    setArmedAt(Date.now());
    // Written via the History API rather than a router.replace, so it does not clobber FloatingChat's
    // own URL writer (the same reason the handoff-param cleanup below `isEmptyLanding` uses it).
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete(PACK_HANDOFF_PARAM);
      window.history.replaceState(window.history.state, '', url.href);
    }
  }, [router.isReady, router.query]);

  // Re-evaluate once at the horizon — the flag has no other end, so without this the indicator it
  // switched on would never switch off.
  useEffect(() => {
    if (armedAt === null) return undefined;
    const timer = setTimeout(() => setArmedAt(null), PACK_HANDOFF_GRACE_MS);
    return () => clearTimeout(timer);
  }, [armedAt]);

  return packHandoffPending(armedAt);
}
